import { AccountStatus, AccountType, AccountModel, IAccountData, AccountQueryOptions } from '@truckdown/accounts';
import context from '@truckdown/components-systems';
import { showMessage } from "../Utilities/Utilities";
import { defineComponent, ref, Ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

const resultsPerPage = 10;

interface IData {
    isLoading: Ref<boolean>;
    duplicateAccountsQuery: Ref<AccountQueryOptions>;
    duplicateAccounts: Ref<AccountModel[]>;
    duplicateAccountsCount: Ref<number>;
    accountId: Ref<string>;

    pageCount: Ref<number>;
    currentQuery: Ref<AccountQueryOptions>;
    resetList: Ref<boolean>;
    
    showDuplicateAccountDropdown: Ref<number>;
}

interface IPageKey {
    lastNumber?: number
}

const getInitialData = function (props: any) {
    let query = {
        limit: resultsPerPage
    };

    return {
        isLoading: ref<boolean>(false),
        duplicateAccountsQuery: ref<AccountQueryOptions>(query),
        duplicateAccountsCount: ref<number>(props.count || 0),
        duplicateAccounts: ref<AccountModel[]>(props?.duplicateAccounts || []),
        accountId: ref<string>(props.accountId || ""),
        pageCount: ref<number>(resultsPerPage),
        resetList: ref<boolean>(false),
        
        showDuplicateAccountDropdown: ref<number>(-1),
        currentQuery: ref<AccountQueryOptions>(query)

    } as IData;
};

const getComputed = function (data: IData) {
    return {
        lastPagerObject: computed(() => {
            if (data.duplicateAccounts.value && data.duplicateAccounts.value.length > 0) {
                let last = (data.duplicateAccounts.value)[data.duplicateAccounts.value.length - 1];
                return {
                    lastNumber: last.ain
                }
            }
        })
    };
};

const getMethods = function (data: IData, props: any) {
    return {
        updateList: async function (updateArgs?: IPageKey) {
            data.showDuplicateAccountDropdown.value = -1;
            await loadDuplicateAccounts(data, data.currentQuery.value, updateArgs);
        },
        getStatus: function (status: AccountStatus) {
            return AccountStatus[status];
        },
        getType: function (type: AccountType) {
            return AccountType[type];
        },
        getAddress: function (account: AccountModel) {
            if (account.info?.address?.city) {
                return account.info.address.city + ", " + account.info.address.regionCode;
            }
            return "";
        },
        toggleDuplicateAccountDropdown: function (index: number) {
            if (data.showDuplicateAccountDropdown.value == index) {
                data.showDuplicateAccountDropdown.value = -1;
            } else {
                data.showDuplicateAccountDropdown.value = index;
            }
        },
        dateDisplay: function (dateUTC: Date | string): Date | string | undefined {
            if (dateUTC) {
                return new Date(dateUTC).toLocaleString();
            } else
                return undefined;
        },
        goToAccount: function (account: AccountModel) {
            window.location.href = `/accounts/admin/${encodeURIComponent(account.ain)}`;
        },
        formatPhoneString: function (phone: string) {
            if (phone?.length == 10) {
                phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
            }
            return phone;
        }
    };
}

const loadDuplicateAccounts = async function (data: IData, query: AccountQueryOptions, page?: IPageKey) {
    if (!data.isLoading.value && data.accountId.value) {
        data.isLoading.value = true;
        try {

            if (page) {
                query.lastNumber = page.lastNumber;
            }

            const accountData = await context.getService<IAccountData>('IAccountData');
            const result = await accountData.getDuplicates(data.accountId.value, query);

            if (result.ok && result.data) {
                query.lastNumber = undefined;
                data.currentQuery.value = query;

                data.duplicateAccounts.value = result.data;
                await setCount(data, query);
            }
            if (!result.ok) {
                showMessage('An error occured while querying duplicate accounts.', "danger", true, true);
            }

        } catch (err) {
            console.error("Error querying duplicate accounts.");
            showMessage('An error occured while querying duplicate accounts.', "danger", true, true);
        } finally {
            data.isLoading.value = false;
        }
    }
};

const setCount = async function (data: IData, options: AccountQueryOptions) {
    try {
        const accountData = await context.getService<IAccountData>('IAccountData');
        const result = await accountData.getDuplicatesCount(data.accountId.value);
        data.duplicateAccountsCount.value = result.data ?? 0;
    } catch (err) {
        console.error("Error getting duplicate accounts count.");
    }
}

export default defineComponent({
    name: "DuplicateAccounts",
    props: {
        accountId: String,
        duplicateAccounts: Array as () => AccountModel[],
        count: Number
    },
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let methods = getMethods(data, props);
        let computed = getComputed(data);

        return {
            ...data,
            ...methods,
            ...computed
        }
    }
})